import React, { useState } from "react";
import { View, ScrollView } from "react-native";
import { Building2, GitMerge } from "lucide-react-native";
import { useFertilityTreatments } from "../FertilityTreatmentsContext";
import { getNextTreatmentScreen } from "../getNextTreatmentScreen";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import {
  CounterInput,
  FertilityTreatmentCycleCard,
  MedicalHistoryTextField,
  QuestionLayout,
} from "@src/packages/medical-history/components";
import { MoonDateField, MoonSelectField } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import type { FertilityTreatmentsStackParamList } from "../FertilityTreatmentsNavigator";
import { hasHistoricalTreatmentDetails } from "../transformFertilityTreatmentsState";

type Cycle = {
  id?: string;
  name: string;
  date: string;
  clinic: string;
  outcome: string | null;
  notes: string;
};

function emptyCycle(): Cycle {
  return { name: "", date: "", clinic: "", outcome: null, notes: "" };
}

export function MedsOnlyTreatmentsScreen({
  mode = "meds_only",
}: {
  mode?: "oi" | "meds_only";
}) {
  const t = useTranslations();
  const { state, updateState } = useFertilityTreatments();
  const navigation =
    useNavigation<NavigationProp<FertilityTreatmentsStackParamList>>();

  const treatmentType = mode;
  const title =
    mode === "oi"
      ? t("med_history.fertility.treatments.oi.cycle_details")
      : t("med_history.fertility.treatments.meds_only.title");
  const counterLabel =
    mode === "oi"
      ? t("med_history.fertility.treatments.oi.reported_cycles")
      : t("med_history.fertility.treatments.meds_only.counter_label");
  const counterDescription =
    mode === "oi"
      ? t("med_history.fertility.treatments.oi.reported_cycles_description")
      : t("med_history.fertility.treatments.meds_only.counter_description");

  const [count, setCount] = useState<number>(() => {
    const existing = (state.treatments || []).filter(
      (treatment) => treatment.type === treatmentType,
    );
    return existing.length > 0 ? existing.length : 1;
  });

  const [cycles, setCycles] = useState<Cycle[]>(() => {
    const existing = (state.treatments || []).filter(
      (treatment) => treatment.type === treatmentType,
    );
    if (existing.length === 0) return [emptyCycle()];
    return existing.map((treatment) => ({
      id: treatment.id,
      name: treatment.name || "",
      date: treatment.date ?? "",
      clinic: treatment.clinic ?? "",
      outcome: treatment.outcome ?? null,
      notes: treatment.notes || "",
    }));
  });

  const ensureLength = (nextCount: number) => {
    setCycles((prev) => {
      const arr = [...prev];
      while (arr.length < nextCount) {
        arr.push(emptyCycle());
      }
      if (arr.length > nextCount) arr.length = nextCount;
      return arr;
    });
  };

  const onNext = () => {
    const otherTreatments = state.treatments.filter(
      (treatment) => treatment.type !== treatmentType,
    );
    const nextTreatments = [
      ...otherTreatments,
      ...cycles.map((c) => ({
        ...(c.id ? { id: c.id } : {}),
        type: treatmentType,
        name: c.name || null,
        date: c.date || null,
        clinic: c.clinic || null,
        outcome: c.outcome || null,
        notes: c.notes || null,
      })),
    ];
    updateState({
      treatments: nextTreatments,
      currentNavigationIndex: (state.currentNavigationIndex ?? 0) + 1,
    });
    const next = getNextTreatmentScreen({
      ...state,
      treatments: nextTreatments,
      currentNavigationIndex: (state.currentNavigationIndex ?? 0) + 1,
    });
    navigation.navigate(next as keyof FertilityTreatmentsStackParamList);
  };

  return (
    <QuestionLayout
      title={title}
      subtitle={t("med_history.fertility.treatments.common.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={onNext}
      saveLabel={t("med_history.fertility.continue")}
    >
      <ScrollView contentContainerStyle={{ gap: 16, paddingBottom: 24 }}>
        <CounterInput
          testID="medical-history-treatment-cycle-counter"
          label={counterLabel}
          description={counterDescription}
          value={count}
          min={1}
          onIncrement={() => {
            const nextCount = count + 1;
            setCount(nextCount);
            ensureLength(nextCount);
          }}
          onDecrement={() => {
            const nextCount = Math.max(1, count - 1);
            setCount(nextCount);
            ensureLength(nextCount);
          }}
          decrementConfirmation={{
            shouldConfirm: () =>
              hasHistoricalTreatmentDetails(cycles[count - 1] ?? {}),
            title: t(
              "med_history.fertility.treatments.common.remove_cycle_title",
            ),
            message: t(
              "med_history.fertility.treatments.common.remove_cycle_message",
            ),
            cancelLabel: t(
              "med_history.fertility.treatments.common.remove_cycle_cancel",
            ),
            confirmLabel: t(
              "med_history.fertility.treatments.common.remove_cycle_confirm",
            ),
          }}
        />

        {cycles.map((c, i) => (
          <FertilityTreatmentCycleCard
            key={i}
            testID={`medical-history-treatment-cycle-${i + 1}`}
            cycleLabel={t("med_history.fertility.treatments.common.cycle", {
              params: { n: String(i + 1) },
            })}
          >
            <View>
              <MedicalHistoryTextField
                label={t("med_history.fertility.treatments.common.name_label")}
                placeholder={t(
                  "med_history.fertility.treatments.common.name_placeholder",
                )}
                value={c.name || ""}
                onChangeText={(text) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = { ...arr[i]!, name: text };
                    return arr;
                  })
                }
                style={{ width: "100%" }}
              />
            </View>
            <View>
              <MoonDateField
                label={t("med_history.fertility.treatments.common.date_label")}
                placeholder={t(
                  "med_history.fertility.treatments.common.date_placeholder",
                )}
                value={c.date}
                onChange={(iso) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = { ...arr[i]!, date: iso };
                    return arr;
                  })
                }
              />
            </View>
            <View>
              <MedicalHistoryTextField
                label={t(
                  "med_history.fertility.treatments.common.clinic_label",
                )}
                placeholder={t(
                  "med_history.fertility.treatments.common.clinic_placeholder",
                )}
                icon={Building2}
                value={c.clinic || ""}
                onChangeText={(text) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = { ...arr[i]!, clinic: text };
                    return arr;
                  })
                }
                style={{ width: "100%" }}
              />
            </View>
            <View>
              <MoonSelectField
                label={t(
                  "med_history.fertility.treatments.common.outcome_label",
                )}
                placeholder={t(
                  "med_history.fertility.treatments.common.outcome_placeholder",
                )}
                value={c.outcome ?? null}
                onChange={(v: string | null) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = { ...arr[i]!, outcome: v };
                    return arr;
                  })
                }
                labelIcon={GitMerge}
                options={[
                  {
                    label: t(
                      "med_history.fertility.treatments.outcome.miscarriage",
                    ),
                    value: "miscarriage",
                  },
                  {
                    label: t(
                      "med_history.fertility.treatments.outcome.chemical_pregnancy",
                    ),
                    value: "chemical_pregnancy",
                  },
                  {
                    label: t(
                      "med_history.fertility.treatments.outcome.ectopic_pregnancy",
                    ),
                    value: "ectopic_pregnancy",
                  },
                  {
                    label: t(
                      "med_history.fertility.treatments.outcome.still_birth",
                    ),
                    value: "still_birth",
                  },
                  {
                    label: t("med_history.fertility.treatments.outcome.other"),
                    value: "other",
                  },
                  {
                    label: t(
                      "med_history.fertility.treatments.outcome.live_birth",
                    ),
                    value: "live_birth",
                  },
                  {
                    label: t(
                      "med_history.fertility.treatments.outcome.multiple_live_births",
                    ),
                    value: "multiple_live_births",
                  },
                ]}
              />
            </View>
            <View>
              <MedicalHistoryTextField
                label={t("med_history.fertility.treatments.common.notes_label")}
                placeholder={t(
                  "med_history.fertility.treatments.common.notes_placeholder",
                )}
                value={c.notes || ""}
                onChangeText={(text) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = { ...arr[i]!, notes: text };
                    return arr;
                  })
                }
                style={{ width: "100%" }}
              />
            </View>
          </FertilityTreatmentCycleCard>
        ))}
      </ScrollView>
    </QuestionLayout>
  );
}

export default MedsOnlyTreatmentsScreen;
